import { FadeIn } from "@xola/ui-kit";
import clsx from "clsx";
import {
    getSortedAddonAndDemographicLineItems,
    isGiftPurchaseItem,
    isLineItemWithNoPrice,
    isTaxFee,
    isVisibleToSeller,
} from "common";
import React from "react";
import { LineItem, LineItemType, PurchaseItem } from "types";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Breakdown } from "../Common/Breakdown";
import { NoTranslate } from "../Common/NoTranslate";
import { PriceAndQuantity as AddonPriceAndQuantity } from "../PurchaseModification/PaymentBreakdown/AddonLineItem";
import { getPriceAndQuantity } from "../PurchaseModification/PaymentBreakdown/utils";
import { useSeller } from "@/hooks/useAccount";
import { isDefaultDemographic } from "@/models/DemographicUnit";

interface CreatePurchaseBreakdownProps {
    item: PurchaseItem;
}

export const CreatePurchaseBreakdown = ({ item }: CreatePurchaseBreakdownProps) => {
    const seller = useSeller();
    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());

    const sortedLineItems = getSortedAddonAndDemographicLineItems(item.lineItems);

    return (
        <FadeIn>
            <Breakdown currency={seller.currency}>
                {sortedLineItems.map((lineItem) => (
                    <BreakdownLineItem key={lineItem.id} lineItem={lineItem} item={item} />
                ))}

                {/* Code from Breakdown.separator because I can't change color of the separator */}
                <tr className={clsx("ui-breakdown-separator")}>
                    <td colSpan={3} className="border-b border-black/10 pb-1" />
                </tr>

                <Breakdown.SubtotalItem
                    info={isVariableDemographicsPackage ? "Group Total" : "Total"}
                    value={item.amount}
                />
            </Breakdown>
        </FadeIn>
    );
};

interface LineItemProps {
    lineItem: LineItem;
    item: PurchaseItem;
}

const shouldTranslateLineItem = (lineItem: LineItem) => {
    if (lineItem.type === LineItemType.DEMOGRAPHIC) {
        return isDefaultDemographic(lineItem.name);
    }

    if ([LineItemType.ADDON, LineItemType.PICKUP].includes(lineItem.type)) {
        return false;
    }

    if (
        [
            LineItemType.PACKAGE_DISCOUNT,
            LineItemType.AFFILIATE_DISCOUNT,
            LineItemType.COUPON_DISCOUNT,
            LineItemType.PARTNER_PACKAGE_DISCOUNT,
        ].includes(lineItem.type)
    ) {
        return true;
    }

    return false;
};

const BreakdownLineItem = ({ lineItem, item }: LineItemProps) => {
    if (!isVisibleToSeller(lineItem)) {
        return null;
    }

    const filterZeroTaxAndFee =
        lineItem.amount === 0 && [LineItemType.TAX, LineItemType.FEE, LineItemType.PARTNER_FEE].includes(lineItem.type);

    if (lineItem.quantity === 0 || filterZeroTaxAndFee) {
        return null;
    }

    const shouldTranslate = shouldTranslateLineItem(lineItem);

    if (
        [
            LineItemType.ADJUSTMENT,
            LineItemType.TAXED_ADJUSTMENT,
            LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
            LineItemType.DEMOGRAPHIC,
            LineItemType.EQUIPMENT,
            LineItemType.WARE,
        ].includes(lineItem.type)
    ) {
        return <DefaultLineItem shouldTranslate={shouldTranslate} lineItem={lineItem} />;
    }

    // Determine if the item is a gift then  should not be styled with a line-through
    const includeInAmount = item && isGiftPurchaseItem(item) ? lineItem.includeInAmount : !lineItem.includeInAmount;

    return (
        <Breakdown.Item
            value={lineItem.amount}
            className={clsx(isTaxFee(lineItem) && includeInAmount && "line-through")}
        >
            <span className={clsx(!shouldTranslate && "no-translate")}>
                {lineItem.type === LineItemType.PICKUP && "Pickup - "}
                {lineItem.name}
            </span>
            <NoTranslate>
                {" "}
                {lineItem.type === LineItemType.ADDON ? (
                    <AddonPriceAndQuantity lineItem={lineItem} item={item} />
                ) : (
                    getPriceAndQuantity(lineItem)
                )}
            </NoTranslate>
        </Breakdown.Item>
    );
};

interface DefaultLineItemProps extends LineItemProps {
    shouldTranslate?: boolean;
}

const DefaultLineItem = ({ shouldTranslate = false, lineItem }: Omit<DefaultLineItemProps, "item">) => {
    const isItemWithNoPrice = isLineItemWithNoPrice(lineItem);

    return (
        <Breakdown.Item value={isItemWithNoPrice ? "--" : lineItem.amount}>
            <>
                <span className={clsx(!shouldTranslate && "no-translate")}>
                    {getLineItemName(lineItem, shouldTranslate)}
                </span>
                <NoTranslate> {getPriceAndQuantity(lineItem)}</NoTranslate>
            </>
        </Breakdown.Item>
    );
};

const getLineItemName = (lineItem: LineItem, shouldTranslate: boolean) => {
    const isItemWithNoPrice =
        (lineItem.type === LineItemType.DEMOGRAPHIC ||
            lineItem.type === LineItemType.EQUIPMENT ||
            lineItem.type === LineItemType.WARE) &&
        !lineItem.price;
    // Show postfix for no-price demographic/ware line items as Name x Quantity.
    if (isItemWithNoPrice && lineItem.quantity) {
        return (
            <>
                <span className={clsx(!shouldTranslate && "no-translate")}>{lineItem.name}</span>
                <NoTranslate> × {lineItem.quantity}</NoTranslate>
            </>
        );
    }

    return <span className={clsx(!shouldTranslate && "no-translate")}>{lineItem.name}</span>;
};
